import { supabase } from '../lib/supabase'

export const API_BASE = process.env.REACT_APP_API_URL || ''

async function jsonAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

const DEFAULT_API_FETCH_TIMEOUT_MS = 10_000

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_API_FETCH_TIMEOUT_MS)
  const headers = await jsonAuthHeaders()
  const mergedHeaders: Record<string, string> = {
    ...headers,
    ...(init.headers as Record<string, string> | undefined),
  }
  const { signal: externalSignal, ...restInit } = init
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort()
    else externalSignal.addEventListener('abort', () => controller.abort(), { once: true })
  }
  try {
    return await fetch(`${API_BASE}${path}`, {
      ...restInit,
      signal: controller.signal,
      headers: mergedHeaders,
    })
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timed out — please try again')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

export const checkBreach = async (email: string) => {
  const response = await fetch(`${API_BASE}/api/breach/check`, {
    method: 'POST',
    headers: await jsonAuthHeaders(),
    body: JSON.stringify({ email })
  })
  return response.json()
}

export const checkThreats = async (domain: string) => {
  const response = await fetch(
    `${API_BASE}/api/threats/check`,
    {
      method: 'POST',
      headers: await jsonAuthHeaders(),
      body: JSON.stringify({ domain })
    }
  )
  return response.json()
}

export const generateBrief = async (data: {
  businessName: string
  country: string
  industry: string
  alertCount: number
  score: number
  accountType: string
}) => {
  const response = await fetch(
    `${API_BASE}/api/brief/generate`,
    {
      method: 'POST',
      headers: await jsonAuthHeaders(),
      body: JSON.stringify(data)
    }
  )
  return response.json()
}

export const sendWhatsApp = async (
  to: string,
  message: string
) => {
  const response = await fetch(
    `${API_BASE}/api/whatsapp/send`,
    {
      method: 'POST',
      headers: await jsonAuthHeaders(),
      body: JSON.stringify({ to, message })
    }
  )
  return response.json()
}

export type AccountDeletionRequestResult =
  | { ok: true; deletionScheduledAt: string }
  | { ok: false; error: string; status: number }

export async function requestAccountDeletion(payload: {
  password: string
  businessName: string
  reason?: string
}): Promise<AccountDeletionRequestResult> {
  const response = await fetch(`${API_BASE}/api/account/request-deletion`, {
    method: 'POST',
    headers: await jsonAuthHeaders(),
    body: JSON.stringify(payload),
  })
  let json: Record<string, unknown> = {}
  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }
  if (!response.ok) {
    const err =
      typeof json.error === 'string' ? json.error : 'Could not schedule account deletion'
    return { ok: false, error: err, status: response.status }
  }
  const deletionScheduledAt = json['deletionScheduledAt']
  if (typeof deletionScheduledAt !== 'string') {
    return { ok: false, error: 'Unexpected server response', status: response.status }
  }
  return { ok: true, deletionScheduledAt }
}

export type AccountCancelDeletionResult =
  | { ok: true }
  | { ok: false; error: string; status: number }

export async function cancelAccountDeletion(): Promise<AccountCancelDeletionResult> {
  const response = await fetch(`${API_BASE}/api/account/cancel-deletion`, {
    method: 'POST',
    headers: await jsonAuthHeaders(),
  })
  let json: Record<string, unknown> = {}
  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }
  if (!response.ok) {
    const err =
      typeof json.error === 'string' ? json.error : 'Could not cancel account deletion'
    return { ok: false, error: err, status: response.status }
  }
  return { ok: true }
}

export type ScoreFactor = {
  label: string
  impact: number
  severity: 'positive' | 'warning' | 'critical'
}

export type SecurityScoreData = {
  overall: number
  email_score: number | null
  domain_score: number | null
  device_score: number | null
  dark_web_score: number | null
  network_score: number | null
  cloud_score: number | null
  momo_score: number | null
  score_change: number
  dimensions_active: string[]
  score_factors: ScoreFactor[]
  calculated_at: string
  next_recalc_at: string
}

export async function getSecurityScore(): Promise<
  | { ok: true; score: SecurityScoreData; fresh: boolean }
  | { ok: false; error: string; status: number }
> {
  const response = await apiFetch('/api/score')
  let json: Record<string, unknown> = {}
  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }

  if (!response.ok) {
    const err = typeof json.error === 'string' ? json.error : 'Failed to fetch score'
    return { ok: false, error: err, status: response.status }
  }

  const score = json['score'] as unknown
  const fresh = Boolean(json['fresh'])
  if (!score || typeof score !== 'object') {
    return { ok: false, error: 'Unexpected server response', status: 500 }
  }
  return { ok: true, score: score as SecurityScoreData, fresh }
}

export async function recalculateScore(): Promise<
  | { ok: true; score: SecurityScoreData }
  | { ok: false; error: string; status: number }
> {
  const response = await apiFetch('/api/score/recalculate', { method: 'POST' })
  let json: Record<string, unknown> = {}
  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }
  if (!response.ok) {
    const err = typeof json.error === 'string' ? json.error : 'Failed to recalculate score'
    return { ok: false, error: err, status: response.status }
  }
  const score = json['score'] as unknown
  if (!score || typeof score !== 'object') {
    return { ok: false, error: 'Unexpected server response', status: 500 }
  }
  return { ok: true, score: score as SecurityScoreData }
}

export type NetworkAssetRow = {
  id: string
  asset_name: string | null
  asset_type: string | null
  data_source: string | null
  os_type: string | null
  device_id: string | null
  last_seen: string | null
  risk_score: number | null
  status: string | null
}

export type NetworkEventRow = {
  id: string
  user_id?: string
  asset_id: string
  target_type?: string
  event_type: string
  severity: string
  risk_score: number
  title: string
  description: string
  rule_matches?: unknown
  raw_data?: unknown
  remote_ip: string | null
  remote_port: number | null
  process_name: string | null
  resolved?: boolean
  resolved_at?: string | null
  created_at: string
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function parseNetworkAssetRow(v: unknown): NetworkAssetRow | null {
  if (!isRecord(v)) return null
  const id = v.id
  if (typeof id !== 'string') return null
  return {
    id,
    asset_name: typeof v.asset_name === 'string' ? v.asset_name : null,
    asset_type: typeof v.asset_type === 'string' ? v.asset_type : null,
    data_source: typeof v.data_source === 'string' ? v.data_source : null,
    os_type: typeof v.os_type === 'string' ? v.os_type : null,
    device_id: typeof v.device_id === 'string' ? v.device_id : null,
    last_seen: typeof v.last_seen === 'string' ? v.last_seen : null,
    risk_score: typeof v.risk_score === 'number' ? v.risk_score : null,
    status: typeof v.status === 'string' ? v.status : null,
  }
}

function parseNetworkEventRow(v: unknown): NetworkEventRow | null {
  if (!isRecord(v)) return null
  const id = v.id
  const asset_id = v.asset_id
  const event_type = v.event_type
  const severity = v.severity
  const risk_score = v.risk_score
  const title = v.title
  const description = v.description
  const created_at = v.created_at
  if (
    typeof id !== 'string' ||
    typeof asset_id !== 'string' ||
    typeof event_type !== 'string' ||
    typeof severity !== 'string' ||
    typeof risk_score !== 'number' ||
    typeof title !== 'string' ||
    typeof description !== 'string' ||
    typeof created_at !== 'string'
  ) {
    return null
  }
  return {
    id,
    asset_id,
    event_type,
    severity,
    risk_score,
    title,
    description,
    remote_ip: typeof v.remote_ip === 'string' ? v.remote_ip : null,
    remote_port: typeof v.remote_port === 'number' ? v.remote_port : null,
    process_name: typeof v.process_name === 'string' ? v.process_name : null,
    created_at,
    user_id: typeof v.user_id === 'string' ? v.user_id : undefined,
    target_type: typeof v.target_type === 'string' ? v.target_type : undefined,
    rule_matches: v.rule_matches,
    raw_data: v.raw_data,
    resolved: typeof v.resolved === 'boolean' ? v.resolved : undefined,
    resolved_at: typeof v.resolved_at === 'string' ? v.resolved_at : null,
  }
}

export type GetNetworkAssetsResult =
  | { ok: true; assets: NetworkAssetRow[] }
  | { ok: false; error: string }

export async function getNetworkAssets(): Promise<GetNetworkAssetsResult> {
  const response = await fetch(`${API_BASE}/api/network/assets`, {
    method: 'GET',
    headers: await jsonAuthHeaders(),
  })
  let json: Record<string, unknown> = {}
  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }
  if (!response.ok) {
    const err = typeof json.error === 'string' ? json.error : 'Could not load network assets'
    return { ok: false, error: err }
  }
  const raw = json.assets
  if (!Array.isArray(raw)) return { ok: true, assets: [] }
  const assets: NetworkAssetRow[] = []
  for (const item of raw) {
    const row = parseNetworkAssetRow(item)
    if (row) assets.push(row)
  }
  return { ok: true, assets }
}

export type GetNetworkEventsResult =
  | { ok: true; events: NetworkEventRow[]; page: number; limit: number }
  | { ok: false; error: string }

export async function getNetworkEvents(params?: {
  severity?: string
  resolved?: boolean
  limit?: number
  page?: number
}): Promise<GetNetworkEventsResult> {
  const q = new URLSearchParams()
  if (params?.severity) q.set('severity', params.severity)
  if (params?.resolved === true) q.set('resolved', 'true')
  if (params?.resolved === false) q.set('resolved', 'false')
  if (params?.limit !== undefined) q.set('limit', String(params.limit))
  if (params?.page !== undefined) q.set('page', String(params.page))
  const qs = q.toString()
  const url = `${API_BASE}/api/network/events${qs ? `?${qs}` : ''}`
  const response = await fetch(url, {
    method: 'GET',
    headers: await jsonAuthHeaders(),
  })
  let json: Record<string, unknown> = {}
  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }
  if (!response.ok) {
    const err = typeof json.error === 'string' ? json.error : 'Could not load network events'
    return { ok: false, error: err }
  }
  const raw = json.events
  const page = typeof json.page === 'number' ? json.page : 1
  const limit = typeof json.limit === 'number' ? json.limit : 20
  if (!Array.isArray(raw)) return { ok: true, events: [], page, limit }
  const events: NetworkEventRow[] = []
  for (const item of raw) {
    const row = parseNetworkEventRow(item)
    if (row) events.push(row)
  }
  return { ok: true, events, page, limit }
}

export type EnrollNetworkDeviceResult =
  | { ok: true; token: string; expiresAt: string; instructions: string }
  | { ok: false; error: string; status: number }

export async function enrollNetworkDevice(payload: {
  asset_type: string
  data_source: string
  label?: string
}): Promise<EnrollNetworkDeviceResult> {
  const response = await fetch(`${API_BASE}/api/network/enroll`, {
    method: 'POST',
    headers: await jsonAuthHeaders(),
    body: JSON.stringify(payload),
  })
  let json: Record<string, unknown> = {}
  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }
  if (!response.ok) {
    const err = typeof json.error === 'string' ? json.error : 'Enrollment failed'
    return { ok: false, error: err, status: response.status }
  }
  const token = json.token
  const expiresAt = json.expiresAt
  const instructions = json.instructions
  if (typeof token !== 'string' || typeof expiresAt !== 'string') {
    return { ok: false, error: 'Unexpected server response', status: response.status }
  }
  return {
    ok: true,
    token,
    expiresAt,
    instructions: typeof instructions === 'string' ? instructions : '',
  }
}

export type ResolveNetworkEventResult = { ok: true } | { ok: false; error: string }

export async function resolveNetworkEvent(id: string): Promise<ResolveNetworkEventResult> {
  const response = await fetch(`${API_BASE}/api/network/events/${encodeURIComponent(id)}/resolve`, {
    method: 'PATCH',
    headers: await jsonAuthHeaders(),
  })
  let json: Record<string, unknown> = {}
  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }
  if (!response.ok) {
    const err = typeof json.error === 'string' ? json.error : 'Could not resolve event'
    return { ok: false, error: err }
  }
  return { ok: true }
}

export type BillingStatus = {
  plan: string
  is_trial: boolean
  trial_active: boolean
  trial_plan: 'starter' | 'business' | null
  trial_expires_at: string | null
  subscription_active: boolean
  subscription_status: string | null
  current_period_end: string | null
  billing_currency: string
  billing_interval: 'monthly' | 'annual'
}

export type StartTrialResult =
  | { ok: true; trial_expires_at: string; message: string }
  | { ok: false; error: string }

export async function startTrial(
  plan: 'starter' | 'business',
  interval: 'monthly' | 'annual' = 'monthly'
): Promise<StartTrialResult> {
  try {
    const res = await fetch(`${API_BASE}/api/billing/start-trial`, {
      method: 'POST',
      headers: await jsonAuthHeaders(),
      body: JSON.stringify({ plan, interval }),
    })
    const data = (await res.json()) as Record<string, unknown>
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Failed to start trial'
      return { ok: false, error: err }
    }
    const expires = data.trial_expires_at
    const message = data.message
    if (typeof expires !== 'string' || typeof message !== 'string') {
      return { ok: false, error: 'Unexpected server response' }
    }
    return { ok: true, trial_expires_at: expires, message }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export type InitializeBillingResult =
  | { ok: true; authorization_url: string; reference: string }
  | { ok: false; error: string }

export async function initializeBilling(
  plan: 'starter' | 'business',
  interval: 'monthly' | 'annual'
): Promise<InitializeBillingResult> {
  try {
    const res = await fetch(`${API_BASE}/api/billing/initialize`, {
      method: 'POST',
      headers: await jsonAuthHeaders(),
      body: JSON.stringify({ plan, interval }),
    })
    const data = (await res.json()) as Record<string, unknown>
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Failed to initialize payment'
      return { ok: false, error: err }
    }
    const url = data.authorization_url
    const ref = data.reference
    if (typeof url !== 'string' || typeof ref !== 'string') {
      return { ok: false, error: 'Unexpected server response' }
    }
    return { ok: true, authorization_url: url, reference: ref }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export type GetBillingStatusResult =
  | { ok: true; status: BillingStatus }
  | { ok: false; error: string }

export async function getBillingStatus(): Promise<GetBillingStatusResult> {
  try {
    const res = await fetch(`${API_BASE}/api/billing/status`, {
      method: 'GET',
      headers: await jsonAuthHeaders(),
    })
    const data = (await res.json()) as Record<string, unknown>
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Failed to fetch billing status'
      return { ok: false, error: err }
    }
    const status = data.status
    if (!status || typeof status !== 'object' || Array.isArray(status)) {
      return { ok: false, error: 'Unexpected server response' }
    }
    const s = status as Record<string, unknown>
    return {
      ok: true,
      status: {
        plan: typeof s.plan === 'string' ? s.plan : 'free',
        is_trial: s.is_trial === true,
        trial_active: s.trial_active === true,
        trial_plan:
          s.trial_plan === 'starter' || s.trial_plan === 'business' ? s.trial_plan : null,
        trial_expires_at: typeof s.trial_expires_at === 'string' ? s.trial_expires_at : null,
        subscription_active: s.subscription_active === true,
        subscription_status:
          typeof s.subscription_status === 'string' ? s.subscription_status : null,
        current_period_end:
          typeof s.current_period_end === 'string' ? s.current_period_end : null,
        billing_currency: typeof s.billing_currency === 'string' ? s.billing_currency : 'USD',
        billing_interval:
          s.billing_interval === 'annual' || s.billing_interval === 'monthly'
            ? s.billing_interval
            : 'monthly',
      },
    }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export type InitializeStripeBillingResult =
  | { ok: true; url: string; session_id: string }
  | { ok: false; error: string }

export async function initializeStripeBilling(
  plan: 'starter' | 'business',
  interval: 'monthly' | 'annual'
): Promise<InitializeStripeBillingResult> {
  try {
    const res = await fetch(`${API_BASE}/api/billing/stripe-initialize`, {
      method: 'POST',
      headers: await jsonAuthHeaders(),
      body: JSON.stringify({ plan, interval }),
    })
    const data = (await res.json()) as Record<string, unknown>
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Failed to initialize payment'
      return { ok: false, error: err }
    }
    const url = data.url
    const sessionId = data.session_id
    if (typeof url !== 'string' || typeof sessionId !== 'string') {
      return { ok: false, error: 'Unexpected server response' }
    }
    return { ok: true, url, session_id: sessionId }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export async function getAssetPrice(
  assetType: string,
  countryCode: string,
  interval: 'monthly' | 'annual' = 'monthly'
): Promise<{ ok: true; price: { amount: number; currency: string } } | { ok: false; error: string }> {
  try {
    const params = new URLSearchParams({ asset_type: assetType, country_code: countryCode, interval })
    const res = await apiFetch(`/api/asset-billing/price?${params}`)
    const data = await res.json()
    if (!res.ok) return { ok: false, error: data.error ?? 'Failed to fetch price' }
    return { ok: true, price: data.price }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export async function checkAssetTrial(
  assetType: string,
  assetValue: string
): Promise<{ ok: true; hasActiveTrial: boolean; isExpired: boolean; trialExpiresAt: string | null } | { ok: false; error: string }> {
  try {
    const res = await apiFetch('/api/asset-billing/check-trial', {
      method: 'POST',
      body: JSON.stringify({ asset_type: assetType, asset_value: assetValue }),
    })
    const data = await res.json()
    if (!res.ok) return { ok: false, error: data.error ?? 'Failed to check trial' }
    return {
      ok: true,
      hasActiveTrial: data.hasActiveTrial === true,
      isExpired: data.isExpired === true,
      trialExpiresAt: typeof data.trialExpiresAt === 'string' ? data.trialExpiresAt : null,
    }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export async function registerAssetTrial(
  assetType: string,
  assetValue: string
): Promise<{ ok: true; trialExpiresAt: string; alreadyUsed: boolean } | { ok: false; error: string }> {
  try {
    const res = await apiFetch('/api/asset-billing/register-trial', {
      method: 'POST',
      body: JSON.stringify({ asset_type: assetType, asset_value: assetValue }),
    })
    const data = await res.json()
    if (!res.ok) return { ok: false, error: data.error ?? 'Failed to register trial' }
    return {
      ok: true,
      trialExpiresAt: typeof data.trialExpiresAt === 'string' ? data.trialExpiresAt : '',
      alreadyUsed: data.alreadyUsed === true,
    }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export const registerAssetTrialForUser = registerAssetTrial

export async function getMonthlyBill(): Promise<{
  ok: true
  bill: {
    total: number
    currency: string
    lineItems: Array<{ asset_id: string; asset_name: string; asset_type: string; amount: number; currency: string; is_trial: boolean }>
  }
} | { ok: false; error: string }> {
  try {
    const res = await apiFetch('/api/asset-billing/bill')
    const data = await res.json()
    if (!res.ok) return { ok: false, error: data.error ?? 'Failed to fetch bill' }
    return { ok: true, bill: data.bill }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export type AssetBillingStatusItem = {
  id: string
  asset_type: string
  asset_value: string
  is_trial: boolean
  trial_expires_at: string | null
  billing_status: string
  monthly_amount: number
  currency: string
  days_remaining: number | null
}

function parseAssetBillingStatusItem(raw: unknown): AssetBillingStatusItem | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  if (typeof o.id !== 'string' || !UUID_RE.test(o.id)) return null
  if (typeof o.asset_type !== 'string' || o.asset_type.length < 1 || o.asset_type.length > 40) return null
  if (!/^[a-z0-9_]+$/i.test(o.asset_type)) return null
  if (typeof o.asset_value !== 'string' || o.asset_value.length < 1 || o.asset_value.length > 500) return null
  if (o.is_trial !== true && o.is_trial !== false) return null
  const trial_expires_at =
    o.trial_expires_at === null
      ? null
      : typeof o.trial_expires_at === 'string' && o.trial_expires_at.length > 0
        ? o.trial_expires_at
        : null
  if (typeof o.billing_status !== 'string' || o.billing_status.length < 1 || o.billing_status.length > 32) return null
  if (!/^[a-z0-9_-]+$/i.test(o.billing_status)) return null
  const monthly_amount =
    typeof o.monthly_amount === 'number' && Number.isFinite(o.monthly_amount) ? o.monthly_amount : null
  if (monthly_amount === null || monthly_amount < 0) return null
  if (typeof o.currency !== 'string' || !/^[A-Z]{3}$/i.test(o.currency)) return null
  const days_remaining =
    o.days_remaining === null
      ? null
      : typeof o.days_remaining === 'number' && Number.isFinite(o.days_remaining) && o.days_remaining >= 0
        ? Math.floor(o.days_remaining)
        : null
  return {
    id: o.id,
    asset_type: o.asset_type,
    asset_value: o.asset_value,
    is_trial: o.is_trial === true,
    trial_expires_at,
    billing_status: o.billing_status,
    monthly_amount,
    currency: o.currency.toUpperCase(),
    days_remaining,
  }
}

export type MonitoringStatus = {
  hasActiveAssets: boolean
  hasExpiredAssets: boolean
  hasGraceAssets: boolean
  hasSoftStoppedAssets: boolean
  graceEndsAt: string | null
  graceTrialExpiredAt: string | null
  expiredCount: number
  graceCount: number
  softStoppedCount: number
}

function parseMonitoringStatus(raw: unknown): MonitoringStatus | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  const b = (k: string) => o[k] === true
  const n = (k: string) => (typeof o[k] === 'number' && Number.isFinite(o[k]) ? Math.floor(o[k] as number) : null)
  const s = (k: string) => {
    const v = o[k]
    return typeof v === 'string' && v.length > 0 ? v : null
  }
  return {
    hasActiveAssets: b('hasActiveAssets'),
    hasExpiredAssets: b('hasExpiredAssets'),
    hasGraceAssets: b('hasGraceAssets'),
    hasSoftStoppedAssets: b('hasSoftStoppedAssets'),
    graceEndsAt: o.graceEndsAt === null ? null : s('graceEndsAt'),
    graceTrialExpiredAt: o.graceTrialExpiredAt === null ? null : s('graceTrialExpiredAt'),
    expiredCount: n('expiredCount') ?? 0,
    graceCount: n('graceCount') ?? 0,
    softStoppedCount: n('softStoppedCount') ?? 0,
  }
}

export async function getMonitoringStatus(): Promise<
  | { ok: true; status: MonitoringStatus }
  | { ok: false; error: string; status: number }
> {
  try {
    const res = await apiFetch('/api/asset-billing/monitoring-status')
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not load monitoring status'
      return { ok: false, error: err, status: res.status }
    }
    if (data.success !== true) return { ok: false, error: 'Unexpected server response', status: 500 }
    const monitoring = parseMonitoringStatus(data.monitoring)
    if (!monitoring) return { ok: false, error: 'Unexpected server response', status: 500 }
    return { ok: true, status: monitoring }
  } catch {
    return { ok: false, error: 'Network error', status: 0 }
  }
}

export type AlertApiRow = {
  id: string
  user_id: string
  category: string
  severity: string
  title: string
  description: string
  recommendation: string | null
  is_read: boolean
  is_locked: boolean
  created_at: string
}

function parseAlertApiRow(raw: unknown): AlertApiRow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  if (typeof o.id !== 'string' || typeof o.user_id !== 'string') return null
  if (typeof o.category !== 'string' || typeof o.severity !== 'string') return null
  if (typeof o.title !== 'string' || typeof o.description !== 'string') return null
  if (o.recommendation !== null && typeof o.recommendation !== 'string') return null
  if (typeof o.is_read !== 'boolean') return null
  if (typeof o.created_at !== 'string') return null
  return {
    id: o.id,
    user_id: o.user_id,
    category: o.category,
    severity: o.severity,
    title: o.title,
    description: o.description,
    recommendation: o.recommendation === null ? null : (o.recommendation as string),
    is_read: o.is_read,
    is_locked: o.is_locked === true,
    created_at: o.created_at,
  }
}

export async function listAlerts(): Promise<
  { ok: true; alerts: AlertApiRow[] } | { ok: false; error: string; status: number }
> {
  try {
    const res = await apiFetch('/api/alerts')
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not load alerts'
      return { ok: false, error: err, status: res.status }
    }
    if (data.success !== true) return { ok: false, error: 'Unexpected server response', status: 500 }
    const raw = data.alerts
    if (!Array.isArray(raw)) return { ok: false, error: 'Unexpected server response', status: 500 }
    const alerts: AlertApiRow[] = []
    for (const row of raw) {
      const p = parseAlertApiRow(row)
      if (p) alerts.push(p)
    }
    return { ok: true, alerts }
  } catch {
    return { ok: false, error: 'Network error', status: 0 }
  }
}

export async function getLockedAlertsCount(): Promise<
  { ok: true; count: number } | { ok: false; error: string; status: number }
> {
  try {
    const res = await apiFetch('/api/alerts/locked-count')
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not load locked count'
      return { ok: false, error: err, status: res.status }
    }
    if (data.success !== true) return { ok: false, error: 'Unexpected server response', status: 500 }
    const c = data.count
    if (typeof c !== 'number' || !Number.isFinite(c) || c < 0) {
      return { ok: false, error: 'Unexpected server response', status: 500 }
    }
    return { ok: true, count: Math.floor(c) }
  } catch {
    return { ok: false, error: 'Network error', status: 0 }
  }
}

export async function expireTrials(): Promise<
  | {
      ok: true
      expired: number
      inGrace: number
      softStopped: number
      skipped: boolean
      monitoring: MonitoringStatus | null
    }
  | { ok: false; error: string }
> {
  try {
    const res = await apiFetch('/api/asset-billing/expire-trials', { method: 'POST' })
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not expire trials'
      return { ok: false, error: err }
    }
    if (data.success !== true) return { ok: false, error: 'Unexpected server response' }
    const expired = data.expired
    if (typeof expired !== 'number' || !Number.isFinite(expired) || expired < 0) {
      return { ok: false, error: 'Unexpected server response' }
    }
    const inGrace = typeof data.inGrace === 'number' && Number.isFinite(data.inGrace) ? Math.floor(data.inGrace) : 0
    const softStopped =
      typeof data.softStopped === 'number' && Number.isFinite(data.softStopped) ? Math.floor(data.softStopped) : 0
    const skipped = data.skipped === true
    const monitoring = parseMonitoringStatus(data.monitoring)
    return {
      ok: true,
      expired: Math.floor(expired),
      inGrace,
      softStopped,
      skipped,
      monitoring,
    }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export async function getAssetBillingStatus(): Promise<
  | {
      ok: true
      assets: AssetBillingStatusItem[]
    }
  | { ok: false; error: string }
> {
  try {
    const res = await apiFetch('/api/asset-billing/status')
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not load asset billing status'
      return { ok: false, error: err }
    }
    if (data.success !== true) return { ok: false, error: 'Unexpected server response' }
    const rawAssets = data.assets
    if (!Array.isArray(rawAssets)) return { ok: false, error: 'Unexpected server response' }
    const assets: AssetBillingStatusItem[] = []
    for (const row of rawAssets) {
      const parsed = parseAssetBillingStatusItem(row)
      if (parsed) assets.push(parsed)
    }
    return { ok: true, assets }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

/** Monitored asset row from GET /asset-billing/my-assets (includes created_at). */
export type MonitoredAssetRow = AssetBillingStatusItem & { created_at: string }

export type NetworkDeviceRow = {
  id: string
  asset_type: string
  asset_value: string
  is_trial: boolean
  trial_expires_at: string | null
  billing_status: string
  monthly_amount: number
  currency: string
  created_at: string
  days_remaining: number | null
  os_type: string | null
  last_seen: string | null
}

function parseMonitoredAssetRow(raw: unknown): MonitoredAssetRow | null {
  const base = parseAssetBillingStatusItem(raw)
  if (!base) return null
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  if (typeof o.created_at !== 'string' || o.created_at.length < 10) return null
  return { ...base, created_at: o.created_at }
}

function parseNetworkDeviceRow(raw: unknown): NetworkDeviceRow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  if (typeof o.id !== 'string' || !UUID_RE.test(o.id)) return null
  if (typeof o.asset_type !== 'string' || o.asset_type.length < 1 || o.asset_type.length > 40) return null
  if (typeof o.asset_value !== 'string' || o.asset_value.length < 1 || o.asset_value.length > 500) return null
  if (o.is_trial !== true && o.is_trial !== false) return null
  const trial_expires_at =
    o.trial_expires_at === null
      ? null
      : typeof o.trial_expires_at === 'string' && o.trial_expires_at.length > 0
        ? o.trial_expires_at
        : null
  if (typeof o.billing_status !== 'string' || o.billing_status.length < 1 || o.billing_status.length > 32) return null
  if (!/^[a-z0-9_-]+$/i.test(o.billing_status)) return null
  const monthly_amount =
    typeof o.monthly_amount === 'number' && Number.isFinite(o.monthly_amount) ? o.monthly_amount : null
  if (monthly_amount === null || monthly_amount < 0) return null
  if (typeof o.currency !== 'string' || !/^[A-Z]{3}$/i.test(o.currency)) return null
  if (typeof o.created_at !== 'string' || o.created_at.length < 10) return null
  const days_remaining =
    o.days_remaining === null
      ? null
      : typeof o.days_remaining === 'number' && Number.isFinite(o.days_remaining) && o.days_remaining >= 0
        ? Math.floor(o.days_remaining)
        : null
  const os_type =
    o.os_type === null || o.os_type === undefined
      ? null
      : typeof o.os_type === 'string'
        ? o.os_type
        : null
  const last_seen =
    o.last_seen === null || o.last_seen === undefined
      ? null
      : typeof o.last_seen === 'string' && o.last_seen.length > 0
        ? o.last_seen
        : null
  return {
    id: o.id,
    asset_type: o.asset_type,
    asset_value: o.asset_value,
    is_trial: o.is_trial === true,
    trial_expires_at,
    billing_status: o.billing_status,
    monthly_amount,
    currency: o.currency.toUpperCase(),
    created_at: o.created_at,
    days_remaining,
    os_type,
    last_seen,
  }
}

export async function addMonitoredAsset(
  assetType: string,
  assetValue: string
): Promise<{ ok: true; asset: MonitoredAssetRow } | { ok: false; error: string }> {
  try {
    const res = await apiFetch('/api/asset-billing/add-asset', {
      method: 'POST',
      body: JSON.stringify({ asset_type: assetType, asset_value: assetValue }),
    })
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not add asset'
      return { ok: false, error: err }
    }
    if (data.success !== true) return { ok: false, error: 'Unexpected server response' }
    const parsed = parseMonitoredAssetRow(data.asset)
    if (!parsed) return { ok: false, error: 'Unexpected server response' }
    return { ok: true, asset: parsed }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export async function removeMonitoredAsset(
  id: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    if (!UUID_RE.test(id.trim())) {
      return { ok: false, error: 'Invalid asset id' }
    }
    const res = await apiFetch(`/api/asset-billing/remove-asset/${encodeURIComponent(id.trim())}`, {
      method: 'DELETE',
    })
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not remove asset'
      return { ok: false, error: err }
    }
    if (data.success !== true) return { ok: false, error: 'Unexpected server response' }
    return { ok: true }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

export async function getMyAssets(): Promise<
  | { ok: true; monitored: MonitoredAssetRow[]; devices: NetworkDeviceRow[] }
  | { ok: false; error: string }
> {
  try {
    const res = await apiFetch('/api/asset-billing/my-assets')
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not load assets'
      return { ok: false, error: err }
    }
    if (data.success !== true) return { ok: false, error: 'Unexpected server response' }
    const rawMon = data.monitored
    const rawDev = data.devices
    if (!Array.isArray(rawMon) || !Array.isArray(rawDev)) {
      return { ok: false, error: 'Unexpected server response' }
    }
    const monitored: MonitoredAssetRow[] = []
    for (const row of rawMon) {
      const p = parseMonitoredAssetRow(row)
      if (p) monitored.push(p)
    }
    const devices: NetworkDeviceRow[] = []
    for (const row of rawDev) {
      const p = parseNetworkDeviceRow(row)
      if (p) devices.push(p)
    }
    return { ok: true, monitored, devices }
  } catch {
    return { ok: false, error: 'Network error' }
  }
}

// --- Marketplace (public bundle list + auth checkout summary; prices from API only) ---

const MARKETPLACE_PUBLIC_FETCH_TIMEOUT_MS = 10_000

async function marketplacePublicFetch(path: string): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), MARKETPLACE_PUBLIC_FETCH_TIMEOUT_MS)
  try {
    return await fetch(`${API_BASE}${path}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timed out — please try again')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

export type BundleItem = { asset_type: string; quantity: number }

export type Bundle = {
  id: string
  bundle_key: string
  name: string
  tagline: string
  description: string
  target_audience: string
  discount_percent: number
  is_featured: boolean
  sort_order: number
  items: BundleItem[]
  base_price: number
  discounted_price: number
  currency: string
  savings: number
}

export type IndividualPrice = {
  asset_type: string
  amount: number
  currency: string
}

export type CartItem = {
  id: string
  type: 'bundle' | 'individual'
  bundle_id?: string
  bundle_key?: string
  name: string
  asset_type?: string
  quantity: number
  unit_price: number
  currency: string
  discount_percent?: number
}

export type CheckoutSummaryItem = {
  label: string
  asset_type: string
  quantity: number
  unit_price: number
  total_price: number
  currency: string
  is_bundle: boolean
  discount_percent?: number
  savings?: number
}

export type CheckoutSummary = {
  items: CheckoutSummaryItem[]
  subtotal: number
  currency: string
  payment_provider: 'paystack' | 'stripe'
  disclaimer: string
}

function parseBundle(raw: unknown): Bundle | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const itemsRaw = o.items
  const items: BundleItem[] = []
  if (Array.isArray(itemsRaw)) {
    for (const it of itemsRaw) {
      if (!it || typeof it !== 'object') continue
      const r = it as Record<string, unknown>
      const at = r.asset_type
      const q = r.quantity
      if (typeof at === 'string' && typeof q === 'number' && Number.isFinite(q)) {
        items.push({ asset_type: at, quantity: q })
      }
    }
  }
  const id = o.id
  const bundle_key = o.bundle_key
  const name = o.name
  if (typeof id !== 'string' || typeof bundle_key !== 'string' || typeof name !== 'string') return null
  return {
    id,
    bundle_key,
    name,
    tagline: typeof o.tagline === 'string' ? o.tagline : '',
    description: typeof o.description === 'string' ? o.description : '',
    target_audience: typeof o.target_audience === 'string' ? o.target_audience : '',
    discount_percent: typeof o.discount_percent === 'number' ? o.discount_percent : 0,
    is_featured: o.is_featured === true,
    sort_order: typeof o.sort_order === 'number' ? o.sort_order : 0,
    items,
    base_price: typeof o.base_price === 'number' ? o.base_price : 0,
    discounted_price: typeof o.discounted_price === 'number' ? o.discounted_price : 0,
    currency: typeof o.currency === 'string' ? o.currency : 'USD',
    savings: typeof o.savings === 'number' ? o.savings : 0,
  }
}

export async function getMarketplaceBundles(countryCode: string): Promise<Bundle[]> {
  const cc = /^[A-Z]{2}$/.test((countryCode || 'US').trim().toUpperCase())
    ? (countryCode || 'US').trim().toUpperCase()
    : 'US'
  try {
    const res = await marketplacePublicFetch(`/api/marketplace/bundles?country_code=${encodeURIComponent(cc)}`)
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) return []
    const raw = data.bundles
    if (!Array.isArray(raw)) return []
    const out: Bundle[] = []
    for (const row of raw) {
      const b = parseBundle(row)
      if (b) out.push(b)
    }
    return out
  } catch {
    return []
  }
}

export async function getIndividualPrices(countryCode: string): Promise<IndividualPrice[]> {
  const cc = (countryCode || 'US').trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(cc)) return []
  try {
    const res = await marketplacePublicFetch(
      `/api/marketplace/individual-prices?country_code=${encodeURIComponent(cc)}`
    )
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) return []
    const raw = data.prices
    if (!Array.isArray(raw)) return []
    const out: IndividualPrice[] = []
    for (const row of raw) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      if (typeof r.asset_type === 'string' && typeof r.amount === 'number' && typeof r.currency === 'string') {
        out.push({ asset_type: r.asset_type, amount: r.amount, currency: r.currency })
      }
    }
    return out
  } catch {
    return []
  }
}

export type CheckoutSummaryRequestItem = {
  type: 'bundle' | 'individual'
  bundle_id?: string
  asset_type?: string
  quantity: number
}

export async function getCheckoutSummary(
  items: CheckoutSummaryRequestItem[]
): Promise<{ ok: true; summary: CheckoutSummary } | { ok: false; error: string; status?: number }> {
  try {
    const res = await apiFetch('/api/marketplace/checkout-summary', {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
    let data: Record<string, unknown> = {}
    try {
      data = (await res.json()) as Record<string, unknown>
    } catch {
      data = {}
    }
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Could not load checkout summary'
      return { ok: false, error: err, status: res.status }
    }
    const itemsRaw = data.items
    const subtotal = data.subtotal
    const currency = data.currency
    const payment_provider = data.payment_provider
    const disclaimer = data.disclaimer
    if (
      !Array.isArray(itemsRaw) ||
      typeof subtotal !== 'number' ||
      typeof currency !== 'string' ||
      (payment_provider !== 'paystack' && payment_provider !== 'stripe') ||
      typeof disclaimer !== 'string'
    ) {
      return { ok: false, error: 'Unexpected server response' }
    }
    const parsedItems: CheckoutSummaryItem[] = []
    for (const row of itemsRaw) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      if (
        typeof r.label === 'string' &&
        typeof r.asset_type === 'string' &&
        typeof r.quantity === 'number' &&
        typeof r.unit_price === 'number' &&
        typeof r.total_price === 'number' &&
        typeof r.currency === 'string' &&
        typeof r.is_bundle === 'boolean'
      ) {
        parsedItems.push({
          label: r.label,
          asset_type: r.asset_type,
          quantity: r.quantity,
          unit_price: r.unit_price,
          total_price: r.total_price,
          currency: r.currency,
          is_bundle: r.is_bundle,
          discount_percent: typeof r.discount_percent === 'number' ? r.discount_percent : undefined,
          savings: typeof r.savings === 'number' ? r.savings : undefined,
        })
      }
    }
    return {
      ok: true,
      summary: {
        items: parsedItems,
        subtotal,
        currency,
        payment_provider,
        disclaimer,
      },
    }
  } catch (err) {
    if (
      err instanceof Error &&
      (err.name === 'AbortError' || err.message === 'Request timed out — please try again')
    ) {
      return { ok: false, error: 'Request timed out — please try again' }
    }
    return { ok: false, error: 'Network error' }
  }
}
